import lang from '@/lang'
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'

import BuhlArchivService, { IBuhlArchivConnectionInfo } from '@/modules/_services/buhl-archiv.service'
import { toast } from '@/modules/base/components/toast/toastConfig'

export enum BuhlArchivConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTED = 'connected',
  ERROR = 'error',
}

export interface BuhlArchivConnectionState {
  info: IBuhlArchivConnectionInfo
  isLoading: boolean
}
const initialState: BuhlArchivConnectionState = {
  info: {
    status: BuhlArchivConnectionStatus.DISCONNECTED,
    email: undefined,
  },
  isLoading: false,
}

export const getConnectionInfo = createAsyncThunk<
  IBuhlArchivConnectionInfo,
  void,
  { rejectValue: string }
>(
  'buhlArchiv/getConnectionInfo',
  async(_, { rejectWithValue }) => {
    try {
      const response = await BuhlArchivService.getConnectionInfo()
      return response
    } catch (error) {
      console.error('Error getting connection information:', error)
      return rejectWithValue(error as string)
    }
  })

export const disconnectFromBuhlArchiv = createAsyncThunk<
  IBuhlArchivConnectionInfo,
  void,
  { rejectValue: string }
>(
  'buhlArchiv/disconnect',
  async(_, { rejectWithValue }) => {
    try {
      const response = await BuhlArchivService.disconnect()
      return response as IBuhlArchivConnectionInfo
    } catch {
      return rejectWithValue(lang.buhlArchiv.connection.errors.connect)
    }
  },
)

const buhlArchivConnectionSlice = createSlice({
  name: 'buhlArchiv/connection',
  initialState,
  reducers: {
    setConnection: (state, action: PayloadAction<IBuhlArchivConnectionInfo>) => {
      state.info = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getConnectionInfo.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getConnectionInfo.fulfilled, (state, action: PayloadAction<IBuhlArchivConnectionInfo>) => {
        state.isLoading = false
        state.info = action.payload
      })
      .addCase(getConnectionInfo.rejected, (state) => {
        state.isLoading = false
        state.info.status = BuhlArchivConnectionStatus.ERROR
      })
      .addCase(disconnectFromBuhlArchiv.pending, (state) => {
        state.isLoading = true
      })
      .addCase(disconnectFromBuhlArchiv.fulfilled, (state, action: PayloadAction<IBuhlArchivConnectionInfo>) => {
        state.isLoading = false
        state.info = action.payload
      })
      .addCase(disconnectFromBuhlArchiv.rejected, (state) => {
        state.isLoading = false
        toast.showToast({
          message: lang.buhlArchiv.connection.errors.disconnect,
          type: 'error',
        })
      })
  },
})

export const {
  setConnection,
} = buhlArchivConnectionSlice.actions

export const selectConnectionInfo = (state: { buhlArchiv: { connection: BuhlArchivConnectionState } }) => state.buhlArchiv.connection.info
export const selectConnectionIsLoading = (state: { buhlArchiv: { connection: BuhlArchivConnectionState } }) => state.buhlArchiv.connection.isLoading

export default buhlArchivConnectionSlice.reducer
